import { z } from "zod";
import { assertUnreachable } from "@lib/utils/assertUnreachable";
import {
  type ActionMessage,
  actionMessage,
  type IncomingActionMessage,
  type IncomingUnconfirmedActionMessage,
  type Reducer,
  type UnconfirmedActionMessage,
  unconfirmedActionMessage,
} from "./core";
import type { RoundAggregate } from "./round/aggregate";

export const numberOrRange = z.union([
  z.number(),
  z.tuple([z.number(), z.number()]),
]);
export type NumberOrRange = z.infer<typeof numberOrRange>;

const performActionSchema = z.object({
  type: z.literal("PerformAction"),
  action: unconfirmedActionMessage,
});

// TS-relaxed: the client queue's message type tolerates unstamped legacy
// payloads (IncomingUnconfirmedActionMessage); at runtime the queue
// migration in SPAStorageAdapter.init stamps them before anything is
// resent, so the strict wire schema is always satisfied.
export type PerformAction = Omit<
  z.infer<typeof performActionSchema>,
  "action"
> & { action: IncomingUnconfirmedActionMessage };

const registerSchema = z.object({
  type: z.literal("Register"),
  lastConfirmedVersion: z.number(),
  supportsActionAck: z.boolean().optional(),
});

export type Register = z.infer<typeof registerSchema>;

const acknowledgeActionsSchema = z.object({
  type: z.literal("AcknowledgeActions"),
  lastConfirmedVersion: z.number(),
});

export type AcknowledgeActions = z.infer<typeof acknowledgeActionsSchema>;

// DESIGN: 'To' semantics over 'From' semantics
// This will be nicer if we ever have to send a message from a server to
// another server
export const messageToServerSchema = z.discriminatedUnion("type", [
  performActionSchema,
  registerSchema,
  acknowledgeActionsSchema,
]);
export type MessageToServer = PerformAction | Register | AcknowledgeActions;

const performActionFailedSchema = z.object({
  type: z.literal("PerformActionFailed"),
  message: z.string(),
  actionId: z.string(),
});

export type PerformActionFailed = z.infer<typeof performActionFailedSchema>;

const confirmActionSchema = z.object({
  type: z.literal("ConfirmActions"),
  actions: z.array(actionMessage),
});

export type ConfirmAction = z.infer<typeof confirmActionSchema>;

export const messageToClientSchema = z.discriminatedUnion("type", [
  performActionFailedSchema,
  confirmActionSchema,
]);

export type MessageToClient = z.infer<typeof messageToClientSchema>;

export interface ClientStorageAdapter {
  performUnconfirmedAction(
    reducer: Reducer,
    action: UnconfirmedActionMessage,
  ): Promise<null | Error>;

  performConfirmedAction(
    reducer: Reducer,
    action: ActionMessage,
  ): Promise<void | Error | ActionError[]>;

  getConfirmedActions(): IncomingActionMessage[];

  getAction(actionId: string): Promise<IncomingUnconfirmedActionMessage | null>;

  getAggregateAtVersion(
    reducer: Reducer,
    version: number,
  ): Promise<RoundAggregate | null>;

  getConfirmedAggregateSync(): RoundAggregate;
  getUnconfirmedAggregateSync(): RoundAggregate;

  getLastConfirmedVersion(): number;

  getUnconfirmedActions(): Promise<
    {
      createdAt: number;
      message: IncomingUnconfirmedActionMessage;
    }[]
  >;

  clearUnconfirmedActions(): Promise<void>;

  removeUnconfirmedAction(reducer: Reducer, actionId: string): Promise<void>;

  getAggregateBeforeUnconfirmedAction(
    reducer: Reducer,
    actionId: string,
  ): Promise<RoundAggregate | Error>;
}

export type ActionError = {
  actionId: string;
  error: Error;
};

export interface ServerStorageAdapter {
  // Accepts legacy-shaped submissions (payload timestamp may be missing —
  // test fixtures, replayed history); the reducer injects the envelope
  // value at application, and the Prisma adapter injects it into every
  // message it returns, so confirmed messages are always fully stamped.
  performAction(
    action: IncomingUnconfirmedActionMessage,
  ): Promise<ActionMessage>;

  markActionFailed?(actionId: string): Promise<void>;

  /**
   * Whether an action id is already persisted. Used by the admission step:
   * clients resend unconfirmed actions after a disconnect (same id, upserted
   * idempotently), and a resend of an already-admitted event must not be
   * re-judged against time-varying policy like feature flags.
   */
  hasConfirmedAction?(actionId: string): Promise<boolean>;

  getConfirmedActionsSince(
    aggregateId: string,
    version: number,
  ): Promise<ActionMessage[]>;

  getLastConfirmedAggregate(aggregateId: string): RoundAggregate | null;

  /** Cheap DB query: returns the highest confirmed action version for an aggregate. */
  getMaxConfirmedActionVersion(aggregateId: string): Promise<number>;

  /** Pull in any externally-written actions (e.g., from workers) into in-memory state. */
  syncExternalActions(aggregateId: string): Promise<void>;
}

export function summarizeMessageToServer(
  msg: MessageToServer,
): Record<string, unknown> {
  switch (msg.type) {
    case "PerformAction":
      return {
        messageType: msg.type,
        actionId: msg.action.id,
        actionType: msg.action.payload.type,
        aggregateId: msg.action.aggregateId,
        actionVersion: "version" in msg.action ? msg.action.version : undefined,
        undoes: msg.action.undoes,
        redoes: msg.action.redoes,
      };
    case "Register":
      return {
        messageType: msg.type,
        lastConfirmedVersion: msg.lastConfirmedVersion,
        supportsActionAck: msg.supportsActionAck ?? false,
      };
    case "AcknowledgeActions":
      return {
        messageType: msg.type,
        lastConfirmedVersion: msg.lastConfirmedVersion,
      };
    default:
      assertUnreachable(msg);
  }
}

export function summarizeMessageToClient(
  msg: MessageToClient,
): Record<string, unknown> {
  switch (msg.type) {
    case "ConfirmActions":
      return {
        messageType: msg.type,
        actionCount: msg.actions.length,
        firstActionId: msg.actions[0]?.id,
        lastActionId: msg.actions[msg.actions.length - 1]?.id,
        firstActionVersion: msg.actions[0]?.version,
        lastActionVersion: msg.actions[msg.actions.length - 1]?.version,
      };
    case "PerformActionFailed":
      return {
        messageType: msg.type,
        actionId: msg.actionId,
        errorMessage: msg.message,
      };
    default:
      assertUnreachable(msg);
  }
}
